import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import {
  useGetInvoicesQuery,
  BillingInvoice,
} from 'store/services/subscription/subscriptionApi';
import './InvoiceHistorySection.css';

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatAmount = (amountCents: number, currency: string) => {
  const amount = (amountCents / 100).toFixed(2);
  const symbol = currency === 'usd' ? '$' : currency.toUpperCase() + ' ';
  return `${symbol}${amount}`;
};

const getStatusClass = (status: string) => {
  switch (status) {
    case 'paid': return 'paid';
    case 'open': return 'open';
    case 'draft': return 'draft';
    case 'uncollectible':
    case 'failed': return 'failed';
    case 'void': return 'void';
    default: return '';
  }
};

const InvoiceHistorySection: React.FC = () => {
  const [allInvoices, setAllInvoices] = useState<BillingInvoice[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasLoadedMore, setHasLoadedMore] = useState(false);

  const { data, isLoading, isFetching } = useGetInvoicesQuery(
    { limit: 10, startingAfter: cursor },
  );

  // Merge initial data with loaded-more data
  const invoices = hasLoadedMore ? allInvoices : (data?.invoices || []);
  const hasMore = hasLoadedMore
    ? (data?.hasMore ?? false)
    : (data?.hasMore ?? false);

  const handleLoadMore = useCallback(() => {
    if (!data?.invoices.length) return;
    const lastInvoice = data.invoices[data.invoices.length - 1];
    setAllInvoices((prev) => {
      const merged = hasLoadedMore ? [...prev, ...data.invoices] : [...data.invoices];
      // Deduplicate by id
      const seen = new Set<string>();
      return merged.filter((inv) => {
        if (seen.has(inv.id)) return false;
        seen.add(inv.id);
        return true;
      });
    });
    setHasLoadedMore(true);
    setCursor(lastInvoice.id);
  }, [data, hasLoadedMore]);

  // When new data arrives after cursor change, append to allInvoices
  React.useEffect(() => {
    if (hasLoadedMore && data?.invoices && cursor) {
      setAllInvoices((prev) => {
        const merged = [...prev, ...data.invoices];
        const seen = new Set<string>();
        return merged.filter((inv) => {
          if (seen.has(inv.id)) return false;
          seen.add(inv.id);
          return true;
        });
      });
    }
  }, [data, cursor, hasLoadedMore]);

  return (
    <div className="billing-inv-section">
      <h4 className="billing-inv-title">Billing History</h4>

      {isLoading ? (
        <div className="billing-inv-loading">Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <div className="billing-inv-empty">No invoices yet.</div>
      ) : (
        <>
          <div className="billing-inv-table">
            <div className="billing-inv-table-header">
              <span>Date</span>
              <span>Amount</span>
              <span>Status</span>
              <span className="billing-inv-desc-col">Description</span>
              <span></span>
            </div>
            {invoices.map((inv) => (
              <div key={inv.id} className="billing-inv-table-row">
                <span className="billing-inv-date">{formatDate(inv.created)}</span>
                <span className="billing-inv-amount">
                  {formatAmount(inv.amountPaid || inv.amountDue, inv.currency)}
                </span>
                <span>
                  <span className={`billing-inv-status ${getStatusClass(inv.status)}`}>
                    {inv.status}
                  </span>
                </span>
                <span className="billing-inv-desc billing-inv-desc-col">
                  {inv.number || 'TradeMagic Elite'}
                </span>
                <span className="billing-inv-download">
                  {inv.pdfUrl ? (
                    <a
                      href={inv.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="billing-inv-download-link"
                      title="Download invoice"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </a>
                  ) : (
                    <span className="billing-inv-no-pdf">--</span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="billing-inv-load-more">
              <button
                className="billing-inv-load-more-btn"
                onClick={handleLoadMore}
                disabled={isFetching}
              >
                {isFetching ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvoiceHistorySection;
